import { useState, useEffect } from "react";
import {
  Typography,
  TextField,
  Button,
  IconButton,
  Tooltip,
  InputAdornment,
  Paper,
  Collapse,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import InfoIcon from "@mui/icons-material/Info";
import { useSnackbar } from "notistack";
import { ApiServices } from "../../../utils/ApiService";

export default function ProfileSetupPage() {
    const payload = {
        id: '',
        phoneNo: '',
        email: '',
        password: ''
    };

    const [profileOpen, setProfileOpen] = useState(true);
    const [isFormValid, setIsFormValid] = useState(false);
    const [rows, setRows] = useState([]);
    const [request, setRequest] = useState({ ...payload });

    const [mobile, setMobile] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [errors, setErrors] = useState({});
    const [userId, setUserId] = useState("");
    const [loading, setLoading] = useState(false);
    const { enqueueSnackbar } = useSnackbar();

    // Profile validation
    const validateFields = () => {
        const newErrors = {};
        if (!/^[0-9]{10}$/.test(mobile)) {
            newErrors.mobile = "Mobile number must be exactly 10 digits!";
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            newErrors.email = "Enter a valid email address!";
        }
        if (password != '' &&
            !/^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,16}$/.test(
                password
            )
        ) {
            newErrors.password =
                "Password must be 8–16 chars, include 1 uppercase letter, 1 number, and 1 symbol!";
        }
        setErrors(newErrors);
        setIsFormValid(Object.keys(newErrors).length === 0);
    };

    useEffect(() => {
        validateFields();
    }, [mobile, email, password]);

    useEffect(() => {
        const user = JSON.parse(localStorage.getItem("user")) || {};
        if (user.phone_no) setMobile(user.phone_no);
        if (user.email) setEmail(user.email);
        if (user.id) setUserId(user.id);
    }, []);

    const handleSaveProfile = async () => {
        if (isFormValid) {
            try {
                setLoading(true);
                request.email = email
                request.phoneNo = mobile
                request.password = password
                request.id = userId
                const res = await ApiServices.post("user/modify_profile", request);
                if (res?.status === 201) {
                    enqueueSnackbar("Profile setup modified successfully!", { variant: "success" });
                    const userData = res?.data;
                    if (userData.phone_no) {
                        setMobile(userData.phone_no);
                    }

                    if (userData.email) {
                        setEmail(userData.email);
                    }

                    setPassword("");
                } else {
                    enqueueSnackbar("Failed to modify profile setup!", { variant: "error" });
                }
            } catch (error) {
                console.error("Fetch error:", error);
                enqueueSnackbar("An error occurred while fetching data.", { variant: "error" });
                setRows([]);
            } finally {
                setLoading(false);
            }
        }
    };

    const getErrorAdornment = (field) =>
        errors[field] ? (
            <InputAdornment position="end">
                <Tooltip title={errors[field]}>
                    <InfoIcon color="error" sx={{ cursor: "pointer" }} />
                </Tooltip>
            </InputAdornment>
        ) : null;

    return (
        <Paper elevation={3} sx={{ p: 3 }}>
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                }}
            >
                <Typography variant="h6">Profile Setup</Typography>
                <IconButton onClick={() => setProfileOpen(!profileOpen)}>
                    {profileOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
            </div>

            <Collapse in={profileOpen} timeout="auto" unmountOnExit>
                <div
                    style={{
                        marginTop: 16,
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "16px",
                    }}
                >
                    {/* Mobile Number */}
                    <div style={{ flex: "1 1 45%", minWidth: "280px" }}>
                        <TextField
                            label="Mobile Number"
                            fullWidth
                            size="small"
                            value={mobile}
                            onChange={(e) => setMobile(e.target.value)}
                            error={!!errors.mobile}
                            InputProps={{
                                endAdornment: getErrorAdornment("mobile"),
                            }}
                        />
                    </div>

                    {/* Email */}
                    <div style={{ flex: "1 1 45%", minWidth: "280px" }}>
                        <TextField
                            label="Email"
                            fullWidth
                            size="small"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            error={!!errors.email}
                            InputProps={{
                                endAdornment: getErrorAdornment("email"),
                            }}
                        />
                    </div>

                    {/* Password */}
                    <div style={{ flex: "1 1 45%", minWidth: "280px", display: "flex", alignItems: "center" }}>
                        <TextField
                            label="Password"
                            type={showPassword ? "text" : "password"}
                            fullWidth
                            size="small"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            error={!!errors.password}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            onClick={() => setShowPassword((prev) => !prev)}
                                            edge="end"
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                        {getErrorAdornment("password")}
                                    </InputAdornment>
                                ),
                            }}
                        />

                        {/* Info Icon aligned to the right of the text field */}
                        <Tooltip title="Leave this blank if you don't want to change your password.">
                            <IconButton
                                size="small"
                                sx={{ marginLeft: "8px" }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <InfoIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </div>

                    {/* Save button */}
                    <div
                        style={{
                            flex: "1 1 45%",
                            minWidth: "280px",
                            display: "flex",
                            justifyContent: "flex-end",
                            alignItems: "center",
                        }}
                    >
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSaveProfile}
                            size="small"
                            disabled={!isFormValid}
                            sx={{ height: "40px", minWidth: "120px" }}
                            startIcon={<SaveIcon />}
                        >
                            Save
                        </Button>
                    </div>
                </div>
            </Collapse>
        </Paper>
    );
}
